import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    FormControlLabel,
    Switch,
    Typography,
} from '@mui/material';
import React, { useContext, useState, useEffect } from 'react';
import { isDuplicateClinicPatientId } from '../../../helper/patientUtils';
import StoreContext from '../../../../react-core-lib/components/app/store-context';

const AddPatientDialog = ({ open, onClose, onSubmit, initialValues, showSnackbar }) => {
    const { authStore } = useContext(StoreContext);
    const [patientName, setPatientName] = useState('');
    const [patientSurname, setPatientSurname] = useState('');
    const [patientEmail, setPatientEmail] = useState('');
    const [clinicPatientId, setClinicPatientId] = useState('');
    const [patientStatus, setPatientStatus] = useState('active'); // toggleable

    useEffect(() => {
        if (initialValues && open) {
            setPatientName(initialValues.patientName || '');
            setPatientSurname(initialValues.patientSurname || '');
            setPatientEmail(initialValues.patientEmail || '');
            setClinicPatientId(initialValues.clinicPatientId || '');
            setPatientStatus(initialValues.patientStatus || 'active');
        } else if (!initialValues && open) {
            setPatientName('');
            setPatientSurname('');
            setPatientEmail('');
            setClinicPatientId('');
            setPatientStatus('active');
        }
    }, [initialValues, open]);

    const handleSave = async () => {
        const name = patientName.trim();
        const surname = patientSurname.trim();
        const email = patientEmail.trim();

        if (!name) {
            showSnackbar?.('Patient name is required.', 'error');
            return;
        }

        if (!surname) {
            showSnackbar?.('Patient surname is required.', 'error');
            return;
        }

        if (email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                showSnackbar?.('Please provide a valid email address.', 'error');
                return;
            }
        }

        const sanitizeField = (value) => {
            return value?.trim() ? value : null;
        };

        const rawData = {
            patientName: name,
            patientSurname: surname,
            patientEmail: sanitizeField(patientEmail),
            clinicPatientId: sanitizeField(clinicPatientId),
            patientStatus,
        };

        // Remove keys with null values
        const cleanedData = Object.fromEntries(Object.entries(rawData).filter(([_, v]) => v !== null));

        if (clinicPatientId?.trim()) {
            try {
                const isDuplicate = await isDuplicateClinicPatientId({
                    bearerToken: authStore.idToken,
                    clinicPatientId: sanitizeField(clinicPatientId),
                    currentPatientId: initialValues?.patientId || null,
                });

                if (isDuplicate) {
                    showSnackbar?.(
                        'Provided PMS ID exists already. Please provide different PMS ID or choose existing patient.',
                        'error',
                    );
                    return;
                }
            } catch (err) {
                console.error('Error while checking for duplicate PMS ID:', err);
                showSnackbar?.('Could not validate PMS ID. Please try again.', 'error');
                return;
            }
        }

        try {
            await onSubmit(cleanedData);
            onClose();
        } catch (err) {
            console.error('Failed to save patient:', err);
            showSnackbar?.('Failed to save patient. Please try again.', 'error');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{initialValues ? 'Edit Patient' : 'Add New Patient'}</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <TextField
                        label="Name"
                        value={patientName}
                        onChange={(e) => setPatientName(e.target.value)}
                        required
                    />
                    <TextField
                        label="Surname"
                        value={patientSurname}
                        onChange={(e) => setPatientSurname(e.target.value)}
                        required
                    />
                    <TextField
                        label="Email"
                        // InputLabelProps={{ shrink: true }}
                        value={patientEmail}
                        onChange={(e) => setPatientEmail(e.target.value)}
                    />
                    <TextField
                        label="Clinic Patient ID"
                        value={clinicPatientId}
                        onChange={(e) => {
                            setClinicPatientId(e.target.value);
                        }}
                    />

                    <Box mt={2}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={patientStatus === 'active'}
                                    onChange={(e) => setPatientStatus(e.target.checked ? 'active' : 'archive')}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography fontFamily="Inter" fontWeight={500}>
                                    Status: {patientStatus === 'active' ? 'Active' : 'Archived'}
                                </Typography>
                            }
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    Cancel
                </Button>
                <Button onClick={handleSave} variant="contained">
                    {initialValues ? 'Save Changes' : 'Add'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddPatientDialog;
